// src/pages/qc/CoaCreateModal.jsx
import { useState, useEffect } from "react";
import * as api from "../../apiService";

const PRODUCT_TYPES = [
  { value: "WB",      label: "Water Based" },
  { value: "BF",      label: "Brake Fluid" },
  { value: "COOLANT", label: "Coolant" },
  { value: "HM",      label: "Hot Melt" },
];

const EMPTY_LINE = {
  batchNo: "", prodDate: "",
  ph: "", viscosity: "", viscTempC: "", solidPct: "", film: "", b4CupVisc: "",
  color: "", density: "", erbp: "", reserveAlkalinity: "", freezingPt: "",
  appearance: "", softeningPt: "", hmViscosity: "",
  remarks: "",
  _manual: false,  // UI-only flag: true = show text input instead of dropdown
};

export function CoaCreateModal({ fgs, sfgs, onSave, onCancel }) {
  const [productType,    setProductType]    = useState("WB");
  const [productId,      setProductId]      = useState("");
  const [customerName,   setCustomerName]   = useState("");
  const [invoiceNo,      setInvoiceNo]      = useState("");
  const [deliveryNoteNo, setDeliveryNoteNo] = useState("");
  const [preparedBy,     setPreparedBy]     = useState("");
  const [verifiedBy,     setVerifiedBy]     = useState("");
  const [remarks,        setRemarks]        = useState("");
  const [lines,          setLines]          = useState([{ ...EMPTY_LINE }]);
  const [products,       setProducts]       = useState([]);
  const [allAnalysis,    setAllAnalysis]    = useState([]);
  const [filteredAnalysis, setFilteredAnalysis] = useState([]);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState(null);

  // Load products + analysis records when product type changes
  useEffect(() => {
    setProductId("");
    setProducts([]);
    setAllAnalysis([]);
    setFilteredAnalysis([]);
    setLines([{ ...EMPTY_LINE }]);

    async function load() {
      try {
        if (productType === "WB") {
          setProducts([...(fgs || []), ...(sfgs || [])].sort((a, b) => a.id.localeCompare(b.id)).map(f => ({ id: f.id, label: f.name || f.id })));
          setAllAnalysis(await api.fetchDailyAnalysis());
        } else if (productType === "BF") {
          const [specs, analyses] = await Promise.all([api.fetchBfSpecs(), api.fetchBfAnalyses()]);
          setProducts(specs.map(s => ({ id: s.productId, label: s.productId })));
          setAllAnalysis(analyses);
        } else if (productType === "COOLANT") {
          const [specs, analyses] = await Promise.all([api.fetchCoolantSpecs(), api.fetchCoolantAnalyses()]);
          setProducts(specs.map(s => ({ id: s.productId, label: s.productId })));
          setAllAnalysis(analyses);
        } else if (productType === "HM") {
          const [grades, analyses] = await Promise.all([api.fetchHmGrades(), api.fetchHmAnalyses()]);
          setProducts(grades.map(g => ({ id: g.id, label: g.displayName || g.id })));
          setAllAnalysis(analyses);
        }
      } catch (e) {
        setError("Failed to load products: " + e.message);
      }
    }
    load();
  }, [productType, fgs, sfgs]);

  // Filter analysis by selected product, reset lines
  useEffect(() => {
    if (!productId) { setFilteredAnalysis([]); return; }
    const filtered = productType === "HM"
      ? allAnalysis.filter(r => r.hmId === productId)
      : allAnalysis.filter(r => r.productId === productId);
    setFilteredAnalysis(filtered);
    setLines([{ ...EMPTY_LINE }]);
  }, [productId]); // intentionally omits allAnalysis/productType — filtered after productId is set

  function addLine() {
    setLines(old => [...old, { ...EMPTY_LINE }]);
  }
  function removeLine(i) {
    if (lines.length === 1) return;
    setLines(old => old.filter((_, idx) => idx !== i));
  }
  function updateLine(i, field, value) {
    setLines(old => old.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  }

  function prefillLine(i, rec) {
    let fields = { batchNo: rec.batchNo || "", prodDate: rec.date || "" };
    if (productType === "WB") {
      fields = { ...fields,
        ph:        rec.ph        != null ? String(rec.ph)        : "",
        viscosity: rec.viscosity != null ? String(rec.viscosity) : "",
        viscTempC: rec.viscTemp  != null ? String(rec.viscTemp)  : "",
        solidPct:  rec.solidPct  != null ? String(rec.solidPct)  : "",
      };
    } else if (productType === "BF") {
      fields = { ...fields,
        color:   rec.color   || "",
        density: rec.density != null ? String(rec.density) : "",
        erbp:    rec.erbp    != null ? String(rec.erbp)    : "",
      };
    } else if (productType === "COOLANT") {
      fields = { ...fields,
        color:             rec.color             || "",
        density:           rec.density           != null ? String(rec.density)           : "",
        reserveAlkalinity: rec.reserveAlkalinity != null ? String(rec.reserveAlkalinity) : "",
        ph:                rec.ph                != null ? String(rec.ph)                : "",
        freezingPt:        rec.freezingPt        != null ? String(rec.freezingPt)        : "",
      };
    } else if (productType === "HM") {
      fields = { ...fields,
        softeningPt: rec.softeningPt != null ? String(rec.softeningPt) : "",
        hmViscosity: rec.viscosity   != null ? String(rec.viscosity)   : "",
      };
    }
    setLines(old => old.map((l, idx) => idx === i ? { ...l, ...fields } : l));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!productId) { setError("Please select a product."); return; }
    setSaving(true); setError(null);
    try {
      const body = {
        productType, productId, customerName, invoiceNo, deliveryNoteNo,
        preparedBy, verifiedBy, remarks,
        batchLines: lines.map(l => ({
          batchNo:           l.batchNo,
          prodDate:          l.prodDate          || null,
          ph:                l.ph                ? parseFloat(l.ph)                : null,
          viscosity:         l.viscosity         ? parseFloat(l.viscosity)         : null,
          viscTempC:         l.viscTempC         ? parseFloat(l.viscTempC)         : null,
          solidPct:          l.solidPct          ? parseFloat(l.solidPct)          : null,
          film:              l.film              || null,
          b4CupVisc:         l.b4CupVisc         ? parseFloat(l.b4CupVisc)         : null,
          color:             l.color             || null,
          density:           l.density           ? parseFloat(l.density)           : null,
          erbp:              l.erbp              ? parseFloat(l.erbp)              : null,
          reserveAlkalinity: l.reserveAlkalinity ? parseFloat(l.reserveAlkalinity) : null,
          freezingPt:        l.freezingPt        ? parseFloat(l.freezingPt)        : null,
          appearance:        l.appearance        || null,
          softeningPt:       l.softeningPt       ? parseFloat(l.softeningPt)       : null,
          hmViscosity:       l.hmViscosity       ? parseFloat(l.hmViscosity)       : null,
          remarks:           l.remarks           || null,
          // _manual is intentionally omitted
        })),
      };
      await onSave(body);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  const isWB         = productType === "WB";
  const isBF         = productType === "BF";
  const isCoolant    = productType === "COOLANT";
  const isHM         = productType === "HM";
  const hasAnalysis  = filteredAnalysis.length > 0;

  function renderBatchCell(l, i) {
    if (!hasAnalysis || l._manual) {
      return (
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <input
            className="form-input" style={{ width: 90 }}
            value={l.batchNo}
            onChange={e => updateLine(i, "batchNo", e.target.value)}
            required
          />
          {hasAnalysis && (
            <button type="button" className="btn-small" title="Back to list"
              style={{ fontSize: 11, padding: "2px 6px" }}
              onClick={() => setLines(old => old.map((ln, idx) =>
                idx === i ? { ...ln, _manual: false, batchNo: "" } : ln))}>
              ↩
            </button>
          )}
        </div>
      );
    }
    return (
      <select
        className="form-input" style={{ width: 140 }}
        value={l.batchNo || ""}
        onChange={e => {
          const val = e.target.value;
          if (val === "__manual__") {
            setLines(old => old.map((ln, idx) =>
              idx === i ? { ...ln, _manual: true, batchNo: "" } : ln));
          } else if (val) {
            const rec = filteredAnalysis.find(r => r.batchNo === val);
            if (rec) prefillLine(i, rec);
          } else {
            updateLine(i, "batchNo", "");
          }
        }}
      >
        <option value="">— select batch —</option>
        {filteredAnalysis.map(r => (
          <option key={r.id} value={r.batchNo}>{r.batchNo}</option>
        ))}
        <option value="__manual__">Enter manually…</option>
      </select>
    );
  }

  return (
    <div className="modal" style={{ width: 880, maxHeight: "90vh", overflowY: "auto" }}>
      <div className="modal-header">
        <div className="modal-title">Generate Certificate of Analysis</div>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          {error && <div className="alert-warn" style={{ marginBottom: 12 }}>{error}</div>}

          {/* Product Type */}
          <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
            {PRODUCT_TYPES.map(pt => (
              <label key={pt.value} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontWeight: productType === pt.value ? 700 : 400 }}>
                <input type="radio" name="productType" value={pt.value}
                  checked={productType === pt.value}
                  onChange={() => setProductType(pt.value)} />
                {pt.label}
              </label>
            ))}
          </div>

          {/* Product + Customer Info */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label className="form-label">Product *</label>
              <select className="form-input" value={productId} onChange={e => setProductId(e.target.value)} required>
                <option value="">— select —</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Customer Name</label>
              <input className="form-input" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="e.g. Al Rawabi Factory" />
            </div>
            <div>
              <label className="form-label">Invoice No</label>
              <input className="form-input" value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)} placeholder="e.g. INV-2026-1234" />
            </div>
            {(isBF || isCoolant) && (
              <div>
                <label className="form-label">Delivery Note No</label>
                <input className="form-input" value={deliveryNoteNo} onChange={e => setDeliveryNoteNo(e.target.value)} placeholder="e.g. DN-2026-0011" />
              </div>
            )}
            <div>
              <label className="form-label">Prepared By</label>
              <input className="form-input" value={preparedBy} onChange={e => setPreparedBy(e.target.value)} placeholder="Name" />
            </div>
            <div>
              <label className="form-label">Verified By</label>
              <input className="form-input" value={verifiedBy} onChange={e => setVerifiedBy(e.target.value)} placeholder="Name" />
            </div>
          </div>

          {/* Batch Lines */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>Batch Lines</span>
            {hasAnalysis && (
              <span style={{ fontSize: 11, color: "#666" }}>
                Select from QC analysis records — values pre-fill automatically. Choose "Enter manually…" for batches not in the database.
              </span>
            )}
          </div>

          <div className="table-wrap" style={{ marginBottom: 12 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Batch No *</th>
                  <th>{isHM ? "Mfg. Date" : "Date"}</th>
                  {isWB  && <><th>pH</th><th>Viscosity (cps)</th><th>Temp (°C)</th><th>Solids (%)</th><th>Film</th><th>B4 Cup (s)</th></>}
                  {isBF  && <><th>Color</th><th>Density (g/cc)</th><th>ERBP (°C)</th></>}
                  {isCoolant && <><th>Color</th><th>Density (g/cc)</th><th>Reserve Alk.</th><th>pH</th><th>Freezing Pt (°C)</th></>}
                  {isHM  && <><th>Appearance</th><th>Softening Pt (°C)</th><th>Viscosity @150°C (cps)</th></>}
                  <th>Remarks</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td>{renderBatchCell(l, i)}</td>
                    <td>
                      <input type="date" className="form-input" style={{ width: 120 }}
                        value={l.prodDate} onChange={e => updateLine(i, "prodDate", e.target.value)} />
                    </td>
                    {isWB && <>
                      <td><input className="form-input" style={{ width: 60 }} type="number" step="0.01"   value={l.ph}         onChange={e => updateLine(i, "ph",         e.target.value)} /></td>
                      <td><input className="form-input" style={{ width: 80 }} type="number" step="0.1"    value={l.viscosity}  onChange={e => updateLine(i, "viscosity",  e.target.value)} /></td>
                      <td><input className="form-input" style={{ width: 60 }} type="number" step="0.1"    value={l.viscTempC}  onChange={e => updateLine(i, "viscTempC",  e.target.value)} /></td>
                      <td><input className="form-input" style={{ width: 60 }} type="number" step="0.01"   value={l.solidPct}   onChange={e => updateLine(i, "solidPct",   e.target.value)} /></td>
                      <td><input className="form-input" style={{ width: 80 }}                             value={l.film}       onChange={e => updateLine(i, "film",       e.target.value)} /></td>
                      <td><input className="form-input" style={{ width: 70 }} type="number" step="0.1"    value={l.b4CupVisc}  onChange={e => updateLine(i, "b4CupVisc",  e.target.value)} /></td>
                    </>}
                    {isBF && <>
                      <td><input className="form-input" style={{ width: 80 }}                             value={l.color}      onChange={e => updateLine(i, "color",      e.target.value)} /></td>
                      <td><input className="form-input" style={{ width: 80 }} type="number" step="0.0001" value={l.density}    onChange={e => updateLine(i, "density",    e.target.value)} /></td>
                      <td><input className="form-input" style={{ width: 70 }} type="number" step="0.1"    value={l.erbp}       onChange={e => updateLine(i, "erbp",       e.target.value)} /></td>
                    </>}
                    {isCoolant && <>
                      <td><input className="form-input" style={{ width: 80 }}                             value={l.color}              onChange={e => updateLine(i, "color",             e.target.value)} /></td>
                      <td><input className="form-input" style={{ width: 80 }} type="number" step="0.0001" value={l.density}            onChange={e => updateLine(i, "density",           e.target.value)} /></td>
                      <td><input className="form-input" style={{ width: 70 }} type="number" step="0.01"   value={l.reserveAlkalinity}  onChange={e => updateLine(i, "reserveAlkalinity",  e.target.value)} /></td>
                      <td><input className="form-input" style={{ width: 60 }} type="number" step="0.01"   value={l.ph}                 onChange={e => updateLine(i, "ph",                e.target.value)} /></td>
                      <td><input className="form-input" style={{ width: 70 }} type="number" step="0.1"    value={l.freezingPt}         onChange={e => updateLine(i, "freezingPt",         e.target.value)} /></td>
                    </>}
                    {isHM && <>
                      <td><input className="form-input" style={{ width: 100 }}                            value={l.appearance}  onChange={e => updateLine(i, "appearance",  e.target.value)} /></td>
                      <td><input className="form-input" style={{ width: 70 }} type="number" step="0.1"    value={l.softeningPt} onChange={e => updateLine(i, "softeningPt", e.target.value)} /></td>
                      <td><input className="form-input" style={{ width: 90 }} type="number" step="0.1"    value={l.hmViscosity} onChange={e => updateLine(i, "hmViscosity", e.target.value)} /></td>
                    </>}
                    <td><input className="form-input" style={{ width: 100 }} value={l.remarks} onChange={e => updateLine(i, "remarks", e.target.value)} /></td>
                    <td>
                      <button type="button" className="btn-small btn-danger"
                        onClick={() => removeLine(i)} disabled={lines.length === 1}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="btn" onClick={addLine} style={{ fontSize: 12 }}>+ Add Batch Line</button>

          {/* COA Remarks */}
          <div style={{ marginTop: 16 }}>
            <label className="form-label">COA Remarks (optional)</label>
            <textarea className="form-input" rows={2} value={remarks} onChange={e => setRemarks(e.target.value)}
              style={{ width: "100%", resize: "vertical" }} placeholder="Any additional notes…" />
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Creating…" : "Create COA"}
          </button>
        </div>
      </form>
    </div>
  );
}
